import { clsx } from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLegalEntityRegions } from '../../../../hooks/useUser';
import { RegionFilter } from './RegionFilter';
import { AllRegionsFilter } from './AllRegionsFilter';
import { SelectedRegionFilter } from './SelectedRegionFilter';
import { AddLocationLink } from './AddLocationLink';
import { RegionFilterOverflow } from './RegionFilterOverflow';
import { components } from '@mosey/api-types';
import { areRegionArraysEqual, getFittingRegions } from './utils';
import { CarouselButton } from './CarouselButton';

type LegalEntityRegionRef = components['schemas']['LegalEntityRegionRef'];

const MAX_REGIONS_BEFORE_OVERFLOW = 20;

export const TaskOverviewRegionFilters = () => {
  const regions = useLegalEntityRegions();
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location_id');
  const measuringContainerRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLUListElement>(null);
  const [fittingRegions, setFittingRegions] = useState<LegalEntityRegionRef[]>(
    [],
  );
  const [scrollPosition, setScrollPosition] = useState<
    'start' | 'middle' | 'end'
  >('start');
  const isOverflow = regions.length > MAX_REGIONS_BEFORE_OVERFLOW;
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateFittingRegions = () => {
    if (measuringContainerRef.current) {
      const newFittingRegions = getFittingRegions(
        regions,
        measuringContainerRef.current,
      );

      if (!areRegionArraysEqual(fittingRegions, newFittingRegions)) {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        resizeTimeoutRef.current = setTimeout(
          setFittingRegions,
          50,
          newFittingRegions,
        );
      }
    }
  };

  useEffect(() => {
    const carouselEl = containerRef.current;

    const calculateVisibility = () => {
      if (carouselEl && !isOverflow) {
        const scrollLeft = carouselEl.scrollLeft;
        const maxScrollLeft = carouselEl.scrollWidth - carouselEl.clientWidth;

        if (scrollLeft === 0) {
          setScrollPosition('start');
        } else if (scrollLeft === maxScrollLeft) {
          setScrollPosition('end');
        } else {
          setScrollPosition('middle');
        }
      }
    };

    if (carouselEl) {
      carouselEl.addEventListener('scroll', calculateVisibility);
    }

    return () => {
      if (carouselEl) {
        carouselEl.removeEventListener('scroll', calculateVisibility);
      }
    };
  });

  useLayoutEffect(() => {
    if (isOverflow && measuringContainerRef.current) {
      const resizeObserver = new ResizeObserver(updateFittingRegions);

      if (measuringContainerRef.current) {
        resizeObserver.observe(measuringContainerRef.current);
      }

      updateFittingRegions();

      return () => {
        resizeObserver.disconnect();
      };
    }
  });

  const advanceCarousel = (direction: 'previous' | 'next') => {
    const carouselEl = containerRef.current;

    if (carouselEl) {
      carouselEl.scrollBy({
        left: carouselEl.clientWidth * (direction === 'next' ? 1 : -1),
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <div className="relative isolate box-border w-full max-w-full px-[30px] py-1">
        {/* This list is for calculating how many filters can fit before overflow */}
        {isOverflow && (
          <ul
            ref={measuringContainerRef}
            aria-hidden="true"
            className="invisible flex h-0 w-full max-w-full grow-0 gap-2 overflow-hidden px-[2px]"
          >
            <AllRegionsFilter />
            <SelectedRegionFilter />
            <RegionFilterOverflow
              regions={regions.filter((region) => {
                return !fittingRegions.find(
                  (fittingRegion) => fittingRegion.id === region.id,
                );
              })}
            />

            {regions
              .filter((region) => region.code !== locationId?.toUpperCase())
              .map(({ region }) => {
                return (
                  <li key={region.id} data-region-code={region.code}>
                    <RegionFilter region={region} />
                  </li>
                );
              })}
          </ul>
        )}

        {!isOverflow && (
          <CarouselButton
            show={scrollPosition !== 'start'}
            onClick={() => advanceCarousel('previous')}
            direction="previous"
          />
        )}

        {/* This is the real list that gets rendered based on the caluclations */}
        <ul
          ref={containerRef}
          className={clsx(
            'flex w-full max-w-full grow-0 gap-2 px-[2px] py-5',
            isOverflow ? 'overflow-hidden' : 'overflow-x-auto',
          )}
        >
          <AllRegionsFilter />
          <SelectedRegionFilter />

          {(isOverflow ? fittingRegions : regions).map(({ region }) => {
            return (
              <li key={region.id}>
                <RegionFilter region={region} />
              </li>
            );
          })}

          {!isOverflow && <AddLocationLink />}

          {isOverflow && (
            <RegionFilterOverflow
              regions={regions.filter((region) => {
                return !fittingRegions.find(
                  (fittingRegion) => fittingRegion.id === region.id,
                );
              })}
            />
          )}
        </ul>

        {!isOverflow && (
          <CarouselButton
            show={scrollPosition !== 'end'}
            direction="next"
            onClick={() => advanceCarousel('next')}
          />
        )}
      </div>
    </>
  );
};
